import { MessageCircle, MessageCircleHeart, Send } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo, useRef } from "react";
import type { TextInput } from "react-native";
import Animated, { useSharedValue, withTiming } from "react-native-reanimated";
import { View, type ViewProps, XStack, YStack } from "tamagui";
import { TKey } from "../../shared/translations/translationKeys";
import { useApi } from "../core/apis";
import { getDayJs } from "../core/dayJs";
import { useStores } from "../core/stores";
import { BlockLoading } from "../ui/Block";
import { Body } from "../ui/Body";
import { Button } from "../ui/Button";
import { Checkbox } from "../ui/Checkbox";
import { Group } from "../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../ui/InfoRow";
import { Input } from "../ui/InputNew";
import { Label } from "../ui/Label";
import { Subtext } from "../ui/Subtext";
import { Title } from "../ui/Title";
import { UserAvatar } from "../ui/UserAvatar";
import { ActivityEventUI } from "./ActivityEventUI";
import { ACTIVITY_PRIORITY_TYPES, ActivityEventType } from "./types";

export const ActivityUI = observer(
  ({
    moduleUid,
    dataUid,
    ...viewProps
  }: { moduleUid: string; dataUid?: string } & ViewProps) => {
    const { modulesStore } = useStores();
    const { activitiesApi } = useApi();

    const module = modulesStore.byId.get(moduleUid);

    const [amount, setAmount] = React.useState(4);
    const [includeAll, setIncludeAll] = React.useState(false);

    React.useEffect(() => {
      if (includeAll || amount > 4) {
        void activitiesApi.getEventsForModuleId(moduleUid, amount, includeAll);
      }
    }, [activitiesApi, moduleUid, includeAll, amount]);

    const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
      if (!module) {
        return [];
      }

      return [
        {
          icon: MessageCircle,
          text: `${module.data.comments} comment(s)`,
        },
      ];
    }, [module, module?.data.comments]);

    if (!module) {
      return <BlockLoading />;
    }

    const events = module.activityEvents.length
      ? module.activityEvents
      : module.recentActivityModuleEvents;

    let lastDayTitle = "";

    const hasMoreActivity = events.length > amount;

    return (
      <View {...viewProps}>
        <YStack
          cursor="pointer"
          position="relative"
          paddingHorizontal={"$large"}
          $gtSm={{
            paddingHorizontal: 0,
          }}
          gap={"$medium"}
        >
          <XStack alignItems="center" justifyContent="space-between">
            <XStack alignItems="center" gap={"$space.small"}>
              <Title>Discuss</Title>
              <MessageCircleHeart />
            </XStack>

            <XStack
              gap="$small"
              alignItems="center"
              cursor="pointer"
              onPress={(e) => {
                e.stopPropagation();
                setIncludeAll(!includeAll);
              }}
            >
              <Body muted>Show activity</Body>
              <Checkbox checked={includeAll} />
            </XStack>
          </XStack>

          <ActivityCommentUI moduleUid={module.uid} dataUid={dataUid} />

          <InfoRow items={infoRowItems} />
        </YStack>

        {events.length > 0 && (
          <View
            paddingTop="$space.larger"
            paddingHorizontal="$space.larger"
            paddingBottom={hasMoreActivity ? undefined : "$space.larger"}
            $gtSm={{
              paddingHorizontal: 0,
            }}
          >
            <Group borderTop={true} borderBottom={hasMoreActivity}>
              {events
                .filter((event) =>
                  dataUid ? event.data.dataUid === dataUid : true,
                )
                .map((event, i) => {
                  if (i >= amount) {
                    return null;
                  }

                  if (
                    !includeAll &&
                    !ACTIVITY_PRIORITY_TYPES.includes(event.data.type)
                  ) {
                    // Happens if you toggle the checkbox twice
                    return null;
                  }

                  let showDayTitle = "";
                  if (event.data.timestamp) {
                    const dayTitle = getDayJs(event.data.timestamp);
                    if (dayTitle !== lastDayTitle) {
                      showDayTitle = lastDayTitle = dayTitle;
                    }
                  }

                  return (
                    <View key={event.uid} width={"100%"}>
                      {!!showDayTitle && (
                        <View marginBottom="$medium">
                          <Label>{showDayTitle}</Label>
                        </View>
                      )}
                      <ActivityEventUI event={event} />
                    </View>
                  );
                })}
            </Group>

            {hasMoreActivity && (
              <Button
                type="secondary"
                onPress={() => {
                  setAmount((x) => x + 10);
                }}
              >
                See more activity
              </Button>
            )}
          </View>
        )}
      </View>
    );
  },
);

const offsetX = 10;
const duration = 300;
const durationComment = 2500;

const CommentReciept = ({
  isVisible,
  onHide,
}: {
  isVisible: boolean;
  onHide: () => void;
}) => {
  const opacity = useSharedValue(0);
  const translateX = useSharedValue(offsetX);

  React.useEffect(() => {
    opacity.value = withTiming(isVisible ? 1 : 0, {
      duration,
    });
    translateX.value = withTiming(isVisible ? 0 : offsetX, {
      duration,
    });
  }, [opacity, isVisible, translateX]);

  React.useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => {
        onHide();
      }, durationComment);

      return () => {
        clearTimeout(t);
      };
    }
  }, [isVisible, onHide]);

  return (
    <Animated.View
      style={{
        position: "absolute",
        right: 16,
        top: 70,
        opacity,
        transform: [{ translateX }],
      }}
    >
      <Subtext>Comment added!</Subtext>
    </Animated.View>
  );
};

export const ActivityCommentUI = observer(
  ({ moduleUid, dataUid }: { moduleUid: string; dataUid?: string }) => {
    const { modulesStore } = useStores();
    const module = modulesStore.byId.get(moduleUid);
    const ref = useRef<TextInput>(null);

    const [showReceipt, setShowReceipt] = React.useState(false);

    const isVisible = !!module && module.plan.showComments;

    if (!isVisible) {
      return null;
    }

    return (
      <View key="activity-portal">
        <View
          key={`comment_${moduleUid}`}
          onPress={() => ref.current?.focus()}
          width="100%"
        >
          <Animated.View
            key="activityInput"
            style={{
              // TODO - move to styled
              borderWidth: 1,
              borderColor: "#dcdcdc",
              backgroundColor: "#ffffff",
              paddingVertical: 12,
              paddingHorizontal: 12,
              width: "100%",
            }}
          >
            <XStack
              gap="$space.medium"
              maxWidth={600}
              width={"100%"}
              alignItems="center"
              alignSelf="center"
            >
              {module.plan.userInPlan?.uid && (
                <UserAvatar userUid={module.plan.userInPlan.uid} />
              )}
              <Input
                ref={ref}
                type="inline"
                submitOnEnter
                submitOnBlur={false}
                blurOnSubmit
                clearOnSubmit
                placeholder={`Comment in ${module.data.title}`}
                icon={Send}
                onSubmitEditing={(e) => {
                  setShowReceipt(true);
                  module.postActivityEvent(
                    ActivityEventType.Comment,
                    TKey.ActivityCommentAdded,
                    { comment: e.nativeEvent.text },
                    dataUid,
                  );
                }}
              />
            </XStack>
          </Animated.View>
        </View>
        <CommentReciept
          isVisible={showReceipt}
          onHide={() => {
            setShowReceipt(false);
          }}
        />
      </View>
    );
  },
);
